import { useState } from "react";
import { Link } from "react-router-dom";
import { preorderHref } from "@/lib/shop";
import { Check, ShoppingBag } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/contexts/CartContext";
import RippleButton from "@/components/motion/RippleButton";
import { sparkFromEvent } from "@/components/motion/ClickSpark";
import { realGallery } from "@/lib/productImage";
import { cn } from "@/lib/utils";

/** Add to basket with ripple, spark and a brief "Added" state. */
export default function AddToBasket({ product, variant, qty = 1, label = "Add to basket", className = "", testid, onAdded }) {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  const disabled = !variant;
  if (variant && variant.stock <= 0) {
    // Sold out: offer a pay later pre-order instead of a dead button.
    return (
      <Link
        to={preorderHref(variant)}
        data-testid={testid ? `${testid}-preorder` : "preorder-link"}
        className={cn("inline-flex items-center justify-center gap-2 h-12 rounded-control px-6 sm:px-8 font-body text-[16px] text-brand-ink bg-[#F3E3A1] hover:bg-[#EBD483] transition-colors whitespace-nowrap", className)}
      >
        Pre-order, pay later
      </Link>
    );
  }
  const onClick = (e) => {
    if (disabled) return;
    sparkFromEvent(e);
    addItem({
      variant_id: variant.id,
      product_id: product.id || product.slug,
      product_name: product.name,
      variant_size: variant.size,
      sku: variant.sku,
      image: realGallery(product)[0],
      unit_price: variant.price,
      mrp: variant.mrp,
      slug: product.slug,
      quantity: qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
    onAdded?.();
  };
  return (
    <RippleButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={cn(
        "h-12 rounded-control px-6 sm:px-8 font-body text-[16px] text-white transition-colors",
        disabled ? "bg-black/30 cursor-not-allowed" : "bg-brand-gold hover:bg-brand-gold-deep",
        className
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={added ? "added" : disabled ? "out" : "add"}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="inline-flex items-center gap-2"
        >
          {added ? <><Check className="w-4 h-4" /> Added</> : disabled ? "Unavailable" : <><ShoppingBag className="w-4 h-4" /> {label}</>}
        </motion.span>
      </AnimatePresence>
    </RippleButton>
  );
}
